import { useCallback, useEffect, useState } from "react";
import { loadSiteSettingRows } from "@/lib/adminSettings";
import {
  ErrorBanner,
  LoadingState,
  PageHeader,
} from "@/pages/admin/components/AdminUI";
import KonaseemaEditor from "./components/KonaseemaEditor";
import TripPlannerEditor from "./components/TripPlannerEditor";

type Tab = "konaseema" | "planner";

export default function AdminKonaseemaTrip() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("konaseema");
  const [rows, setRows] = useState<Record<string, unknown>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await loadSiteSettingRows());
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load the guide content.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tabClass = (active: boolean) =>
    `inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
      active ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-100"
    }`;

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Konaseema & Trip Planner"
        subtitle="Edit the travel-guide copy, photos, seasons and activities, plus the itinerary pool and ready-made presets — updates go live on the website immediately."
      />

      {error ? (
        <div className="mb-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="inline-flex items-center gap-1 bg-background-50 border border-background-200 rounded-full p-1 mb-6">
        <button type="button" onClick={() => setTab("konaseema")} className={tabClass(tab === "konaseema")}>
          <i className="ri-road-map-line"></i> Konaseema Guide
        </button>
        <button type="button" onClick={() => setTab("planner")} className={tabClass(tab === "planner")}>
          <i className="ri-route-line"></i> Trip Planner
        </button>
      </div>

      {loading ? (
        <LoadingState label="Loading guide content…" />
      ) : tab === "konaseema" ? (
        <KonaseemaEditor initial={rows.konaseema} />
      ) : (
        <TripPlannerEditor initial={rows.trip_planner} />
      )}
    </div>
  );
}